/**
 * Helper functions for the STISSA Technology LLP (TISSA Technology) website
 */

/**
 * Format date to a readable string
 * @param {Date|string} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const defaultOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  };
  
  /**
   * Truncate text to a specified length
   * @param {string} text - Text to truncate
   * @param {number} length - Maximum length
   * @param {string} suffix - String to append to truncated text
   * @returns {string} Truncated text
   */
  export const truncateText = (text, length = 100, suffix = '...') => {
    if (!text) return '';
    if (text.length <= length) return text;
    
    return text.substring(0, length).trim() + suffix;
  };
  
  /**
   * Scroll to element with smooth animation
   * @param {string} elementId - ID of element to scroll to
   * @param {Object} options - Scroll options
   */
  export const scrollToElement = (elementId, options = {}) => {
    const element = document.getElementById(elementId);
    if (!element) return;
    
    const defaultOptions = {
      behavior: 'smooth',
      block: 'start'
    };
    
    element.scrollIntoView({ ...defaultOptions, ...options });
  };
  
  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} Whether email is valid
   */
  export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  /**
   * Validate phone number format
   * @param {string} phone - Phone number to validate
   * @returns {boolean} Whether phone number is valid
   */
  export const isValidPhone = (phone) => {
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/[\s()-]/g, ''));
  };
  
  /**
   * Generate a unique ID
   * @returns {string} Unique ID
   */
  export const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };
  
  /**
   * Format currency
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code
   * @returns {string} Formatted currency string
   */
  export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };
  
  /**
   * Get URL parameters
   * @returns {Object} Object containing URL parameters
   */
  export const getUrlParams = () => {
    const params = {};
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    
    for (const [key, value] of urlParams.entries()) {
      params[key] = value;
    }
    
    return params;
  };
  
  /**
   * Set page title
   * @param {string} title - Page title
   */
  export const setPageTitle = (title) => {
    document.title = `${title} | STISSA Technology LLP (TISSA Technology)`;
  };
  
  export default {
    formatDate,
    truncateText,
    scrollToElement,
    isValidEmail,
    isValidPhone,
    generateId,
    formatCurrency,
    getUrlParams,
    setPageTitle
  };